import React, { useState } from 'react';
import { ContactModal } from './ContactModal';
import { SiteNav } from './SiteNav';
import { Logo } from './Logo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { examplePdfUrl } from '../mock';

const faqs = [
  {
    q: "What is included in the Schedule of Works package?",
    a: "You receive a clearly structured, trade by trade Schedule of Works prepared to industry best practice. It is designed to allow contractors to price on a like for like basis and reduce ambiguity, omissions, and variations."
  },
  {
    q: "What else do I get besides the Schedule of Works?",
    a: "In addition to the Schedule of Works, you also receive a workmanship specification, a professional covering letter to issue to contractors, and a tender return form. Together these documents create a complete and professional tender pack."
  },
  {
    q: "What is the workmanship specification?",
    a: "The workmanship specification sets out quality standards, materials expectations, and installation requirements. It ensures contractors understand the level of finish expected and reduces disputes over quality during construction."
  },
  {
    q: "What is the purpose of the professional covering letter?",
    a: "The covering letter explains the tender process, expectations, return deadlines, and how the Schedule of Works should be priced. It positions you professionally and sets clear rules for engagement with contractors."
  },
  {
    q: "What is the tender return form used for?",
    a: "The tender return form standardises how contractors submit their prices. It makes it easy to compare quotes, identify exclusions, confirm programme durations, and check compliance with your requirements."
  },
  {
    q: "Who is this Schedule of Works suitable for?",
    a: "It is suitable for property investors, developers, landlords, and homeowners carrying out refurbishments, conversions, or extensions who want cost clarity and control before starting works."
  },
  {
    q: "Is this suitable for contractors to price accurately?",
    a: "Yes. The documents are structured so contractors can price each trade and item clearly. This reduces assumptions, minimises provisional sums, and leads to more accurate and comparable tenders."
  },
  {
    q: "Does this replace drawings or specifications from an architect?",
    a: "No. The Schedule of Works complements drawings and design information. It focuses on scope, inclusions, and quality, whereas drawings show layout and design intent. Used together, they significantly improve tender outcomes."
  },
  {
    q: "Will this help reduce variations during the build?",
    a: "Yes. A clear Schedule of Works reduces misunderstandings and scope gaps, which are the main causes of variations and cost overruns during construction."
  },
  {
    q: "Is this aligned with professional best practice?",
    a: "Yes. The structure and approach follow quantity surveying and construction best practice, similar to how works are defined for professional tendering and cost control."
  },
  {
    q: "Can I use this for multiple contractors?",
    a: "Yes. The documents are designed to be issued to multiple contractors so you can compare pricing fairly and confidently select the best value option."
  },
  {
    q: "Do I need construction experience to use this?",
    a: "No. The documents are written clearly and logically so that non technical clients can still run a professional tender process with confidence."
  }
];

export const FAQPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleExampleDownload = () => {
    window.open(examplePdfUrl, '_blank');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Navigation */}
      <SiteNav cta="estimate" />

      {/* Main Content */}
      <main className="pt-28 pb-20" data-testid="faq-main-content">
        <div className="content-container max-w-4xl">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mb-8 transition-colors"
            style={{ color: 'var(--color-muted)' }}
            data-testid="faq-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>

          {/* Page Header */}
          <div className="mb-12">
            <h1 style={{ color: 'var(--color-navy)' }} className="mb-4" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-lg" style={{ color: 'var(--color-muted)' }}>
              Everything you need to know about the Schedule of Works tender pack.
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="card px-6 py-0 transition-colors"
                style={{ border: '1px solid var(--color-border)' }}
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold py-5 hover:no-underline" style={{ color: 'var(--color-text)' }}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="leading-relaxed pb-5 pt-0" style={{ color: 'var(--color-muted)' }}>
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA Section */}
          <div className="mt-16 card text-center p-12" style={{ backgroundColor: 'var(--color-bg)', border: 'none' }}>
            <h2 className="mb-4" style={{ color: 'var(--color-navy)' }}>
              Ready to get started?
            </h2>
            <p className="text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--color-muted)' }}>
              Get your professional Schedule of Works prepared by experts and receive accurate, comparable pricing from contractors.
            </p>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="btn-primary px-8 py-3"
              data-testid="faq-cta-btn"
            >
              Get Your Schedule of Works
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: 'white', borderTop: '1px solid var(--color-border)' }}>
        <div className="content-container">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Logo variant="dark" />
            </div>
            <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Professional Schedules of Work for UK projects</p>
            <div className="flex justify-center space-x-6 pt-4">
              <a href="/terms" style={{ color: 'var(--color-muted)' }} className="transition-colors">
                Terms and Conditions
              </a>
              <button onClick={() => navigate('/faq')} style={{ color: 'var(--color-muted)', background: 'none', border: 'none', cursor: 'pointer' }} className="transition-colors">
                FAQ
              </button>
              <button onClick={() => setIsContactModalOpen(true)} style={{ color: 'var(--color-muted)', background: 'none', border: 'none', cursor: 'pointer' }} className="transition-colors">
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal open={isContactModalOpen} onOpenChange={setIsContactModalOpen} />
    </div>
  );
};
